/**
 * A5 v2 competitor content analysis — detail page.
 *
 * Sections:
 *   - Hero (keyword, status, Export PDF)
 *   - Progress stepper — visible while running
 *   - Warnings banner
 *   - Recommended angle + outline
 *   - SERP archetype, clusters, gaps, pages, patterns
 */
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import {
  AlertOctagon,
  AlertTriangle,
  Check,
  Circle,
  Download,
  ExternalLink,
  Info,
  Layers,
  ListTree,
  Loader2,
  Search,
  Sparkles,
  Target,
} from 'lucide-react';
import { Page, PageHeader } from '@/components/ui/page';
import { Section, SectionHeader, SectionTitle, SectionDescription } from '@/components/ui/section';
import { StatTile } from '@/components/ui/stat-tile';
import { EmptyState } from '@/components/ui/empty';
import { DetailError } from '@/components/detail-page';
import { Spinner } from '@/components/spinner';
import { StatusPill } from '@/components/status-pill';
import { Surface } from '@/components/ui/surface';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast, toastError } from '@/components/ui/toast';
import { cn } from '@/lib/cn';
import { relTime } from '@/lib/format';
import { downloadAuthed } from '@/lib/download';
import { useAuthStore } from '@/lib/auth';
import { isFailed, isInProgress, isSuccess } from '@/lib/status';
import { StrategyPanel } from '@/features/competitor-analyses/components/strategy-panel';
import {
  useCompetitorAnalysis,
  useCompetitorAnalysisClusters,
  useCompetitorAnalysisGaps,
  useCompetitorAnalysisInsights,
  useCompetitorAnalysisPages,
} from '@/features/competitor-analyses/use-competitor-analysis';
import type {
  CompetitorAnalysis,
  CompetitorAnalysisCluster,
  CompetitorAnalysisGap,
  CompetitorAnalysisPage,
  CompetitorClusterClassification,
} from '@seo/types';

// Phase steps shown while the analysis runs, written for a marketer.
const PHASE_STEPS: Array<{ status: string; label: string; blurb: string }> = [
  { status: 'discovering', label: 'Finding top pages', blurb: 'Pulling the top-ranking search results' },
  { status: 'fetching', label: 'Reading competitor pages', blurb: 'Loading each page in full' },
  { status: 'extracting', label: 'Studying content', blurb: 'Checking structure, author info, and depth' },
  { status: 'embedding', label: 'Understanding themes', blurb: 'Making sense of what each page covers' },
  { status: 'clustering', label: 'Grouping topics', blurb: 'Spotting patterns across pages' },
  { status: 'scoring', label: 'Comparing stats', blurb: 'Word count, images, tables, freshness' },
  { status: 'synthesizing', label: 'Grading each page', blurb: 'Where every result wins or loses' },
  { status: 'critiquing', label: 'Writing your outline', blurb: 'Final plan for your article' },
];

export function CompetitorAnalysisDetailRoute(): JSX.Element {
  const params = useParams({ strict: false }) as { analysisId?: string };
  const id = params.analysisId ?? '';
  const [showAllGaps, setShowAllGaps] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Active tab lives in the URL (?tab=research) — deep-linkable and honest
  // about which PDF the export button will produce.
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { tab?: 'strategy' | 'research' };
  const activeTab: 'strategy' | 'research' = search.tab ?? 'strategy';
  const setActiveTab = (v: 'strategy' | 'research'): void => {
    void navigate({
      search: (prev) =>
        ({
          ...(prev as object),
          tab: v === 'strategy' ? undefined : v,
        }) as never,
    });
  };
  const { toast } = useToast();
  const accessToken = useAuthStore((s) => s.accessToken);
  const tenant = useAuthStore((s) => s.tenant);

  const analysis = useCompetitorAnalysis(id);
  const pages = useCompetitorAnalysisPages(id);
  const clusters = useCompetitorAnalysisClusters(id);
  const gaps = useCompetitorAnalysisGaps(id, { all: showAllGaps });
  const insights = useCompetitorAnalysisInsights(
    id,
    !!analysis.data && isSuccess(analysis.data.status),
  );

  // A missing param means the URL is malformed — treat as not-found rather
  // than rendering a generic error over queries that never ran.
  if (!id) {
    return (
      <Page>
        <EmptyState
          title="This analysis isn't available"
          description="The link is missing its analysis id."
        />
      </Page>
    );
  }

  if (analysis.isLoading) {
    return (
      <Page>
        <div className="flex justify-center p-10">
          <Spinner />
        </div>
      </Page>
    );
  }

  if (analysis.error || !analysis.data) {
    return (
      <Page>
        <DetailError
          error={analysis.error}
          entityKind="analysis"
          backTo="/competitor-analyses"
          backLabel="Content analysis"
        />
      </Page>
    );
  }

  const a = analysis.data;
  const pageRows = pages.data?.items ?? [];
  const clusterRows = clusters.data?.items ?? [];
  const gapRows = gaps.data?.items ?? [];
  const gapTotal = gaps.data?.total ?? gapRows.length;
  const gapHasMore = gaps.data?.has_more === true;

  const running = isInProgress(a.status);
  const failed = isFailed(a.status);
  const succeeded = isSuccess(a.status);
  const patterns = (a.patterns ?? {}) as Record<string, unknown>;
  const outlines = Array.isArray(a.outline_hints) ? a.outline_hints : [];
  const excluded = pageRows.filter((p) => !p.included_in_analysis).length;
  const included = pageRows.filter((p) => p.included_in_analysis).length;

  const handleExportPdf = async (): Promise<void> => {
    if (!succeeded || exporting) return;
    setExporting(true);
    try {
      const safe = a.keyword
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 40);
      const view = activeTab === 'research' ? 'research' : 'strategy';
      await downloadAuthed({
        url: `/v1/competitor-analyses/${a.id}/report.pdf?view=${view}`,
        filename: `competitor-${view}-${safe || a.id.slice(0, 8)}.pdf`,
        accessToken,
        tenantId: tenant?.id,
      });
      toast({
        title: view === 'research' ? 'Research PDF downloaded' : 'Strategy PDF downloaded',
      });
    } catch (e) {
      toastError(toast, e);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Page>
      <PageHeader
        title={a.keyword}
        description={
          <span className="text-muted-foreground">
            {running
              ? 'Analyzing the top search results — this usually takes a couple of minutes.'
              : succeeded
                ? `We looked at the top-ranking pages for "${a.keyword}" and put together a plan to outrank them.`
                : failed
                  ? "This analysis didn't finish. See the error below."
                  : `Content analysis · ${a.language.toUpperCase()} · ${a.country.toUpperCase()}`}
          </span>
        }
        actions={
          <div className="flex items-center gap-2">
            <StatusPill status={a.status} />
          </div>
        }
      />
      {/* Running state — progress + phase */}
      {running ? <ProgressCard analysis={a} /> : null}

      {/* Failure banner */}
      {failed ? (
        <Section>
          <div className="flex items-start gap-3 rounded-md border border-destructive/40 bg-destructive/5 p-4">
            <AlertOctagon className="mt-0.5 h-4 w-4 flex-shrink-0 text-destructive" />
            <div className="text-sm">
              <div className="font-semibold text-destructive">Analysis didn't finish</div>
              <div className="mt-1 text-muted-foreground">
                {a.failure_reason ?? 'Something went wrong. Try starting a new analysis.'}
              </div>
            </div>
          </div>
        </Section>
      ) : null}

      {/* Quick stats — even during running so users see something */}
      {(succeeded || running || failed) && (a.competitors_included ?? 0) > 0 ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <StatTile
            label="Pages analyzed"
            value={a.competitors_included ?? 0}
            icon={Search}
            hint={excluded > 0 ? `${excluded} skipped` : undefined}
          />
          <StatTile label="Topic groups" value={a.clusters_found ?? 0} icon={Layers} />
          <StatTile
            label="Gaps to fill"
            value={a.gaps_found ?? 0}
            icon={AlertTriangle}
            tone={(a.gaps_found ?? 0) > 0 ? 'warning' : 'muted'}
          />
          <StatTile label="Outline sections" value={outlines.length} icon={ListTree} />
          <StatTile
            label="Notes"
            value={a.warnings?.length ?? 0}
            icon={Info}
            tone={(a.warnings?.length ?? 0) > 0 ? 'warning' : 'muted'}
          />
        </div>
      ) : null}

      {/* Warnings expander */}
      {Array.isArray(a.warnings) && a.warnings.length > 0 ? (
        <WarningsCard warnings={a.warnings} />
      ) : null}

      {succeeded ? (
        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v === 'research' ? 'research' : 'strategy')}
          className="w-full"
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <TabsList className="w-auto justify-start">
              <TabsTrigger value="strategy">Strategy</TabsTrigger>
              <TabsTrigger value="research">Research</TabsTrigger>
            </TabsList>
            <Button
              variant="outline"
              size="sm"
              disabled={exporting}
              onClick={() => void handleExportPdf()}
            >
              {exporting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              {exporting
                ? 'Building PDF…'
                : activeTab === 'research'
                  ? 'Export Research PDF'
                  : 'Export Strategy PDF'}
            </Button>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            {activeTab === 'research'
              ? 'Research PDF includes pages, gaps, topic groups, and patterns from this tab.'
              : 'Strategy PDF includes the advise-first plan from this tab (how to beat the SERP).'}
          </p>

          <TabsContent value="strategy" className="mt-4 space-y-4">
            <StrategyPanel
              data={insights.data}
              loading={insights.isLoading}
              error={insights.error instanceof Error ? insights.error : null}
            />
          </TabsContent>

          <TabsContent value="research" className="mt-4 space-y-4">
            {/* Recommended angle — the top-of-page action */}
            {a.recommended_angle ? (
              <Section>
                <SectionHeader>
                  <div>
                    <SectionTitle className="flex items-center gap-2">
                      <Target className="h-4 w-4 text-primary" />
                      Your angle
                    </SectionTitle>
                    <SectionDescription>
                      The one thing your article should nail that the top-{included ?? '?'} pages
                      miss.
                    </SectionDescription>
                  </div>
                </SectionHeader>
                <Surface tone="muted" className="border-l-4 border-l-primary p-4">
                  <p className="whitespace-pre-line text-[15px] leading-relaxed">
                    {a.recommended_angle}
                  </p>
                </Surface>
              </Section>
            ) : null}

            {/* Outline hints — the writer's checklist */}
            {outlines.length > 0 ? <OutlineCard outlines={outlines as OutlineHint[]} /> : null}

            {/* SERP archetype */}
            {a.serp_archetype_summary ? (
              <Section>
                <SectionHeader>
                  <div>
                    <SectionTitle className="flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-muted-foreground" />
                      What Google's rewarding for this search
                    </SectionTitle>
                    <SectionDescription>
                      Common traits across the top-{included ?? '?'} pages that are ranking today.
                    </SectionDescription>
                  </div>
                </SectionHeader>
                <p className="whitespace-pre-line text-sm leading-relaxed text-muted-foreground">
                  {a.serp_archetype_summary}
                </p>
              </Section>
            ) : null}

            {/* Clusters */}
            <ClustersSection clusters={clusterRows} loading={clusters.isLoading} />

            {/* Gaps */}
            <GapsSection
              gaps={gapRows}
              loading={gaps.isLoading}
              showAll={showAllGaps}
              total={gapTotal}
              hasMore={gapHasMore || gapTotal > gapRows.length}
              onToggleShowAll={() => setShowAllGaps((v) => !v)}
            />

            {/* Pages */}
            <PagesSection pages={pageRows} loading={pages.isLoading} />

            {/* Patterns — deterministic aggregates */}
            {Object.keys(patterns).length > 0 ? <PatternsCard patterns={patterns} /> : null}
          </TabsContent>
        </Tabs>
      ) : null}
    </Page>
  );
}

/* ────────────────────────── Progress card ────────────────────────── */

function ProgressCard({ analysis }: { analysis: CompetitorAnalysis }): JSX.Element {
  const currentIdx = PHASE_STEPS.findIndex((s) => s.status === analysis.status);
  const phaseLabel =
    PHASE_STEPS.find((s) => s.status === analysis.status)?.label ??
    'Starting up';
  const progress = (analysis.progress ?? {}) as Record<string, unknown>;
  const pagesFetched = readNumber(progress, 'pages_fetched');
  const clustersFound = readNumber(progress, 'clusters_found');

  return (
    <Section>
      <div className="rounded-lg border border-primary/30 bg-primary/5 p-4">
        <div className="flex items-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-primary">
              {phaseLabel}…
            </div>
            <div className="text-xs text-muted-foreground">
              Started {relTime(analysis.started_at ?? analysis.created_at)} · this page updates on its own.
              {pagesFetched != null ? ` · ${pagesFetched} pages read` : ''}
              {clustersFound != null ? ` · ${clustersFound} topic groups` : ''}
            </div>
          </div>
        </div>

        <ol className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-8">
          {PHASE_STEPS.map((step, i) => {
            const done = i < currentIdx;
            const active = i === currentIdx;
            const pending = i > currentIdx;
            return (
              <li
                key={step.status}
                className={cn(
                  'flex flex-col gap-1 rounded-md border p-2 text-[11px]',
                  done && 'border-emerald-500/30 bg-emerald-500/5 text-emerald-700 dark:text-emerald-400',
                  active && 'border-primary/40 bg-primary/10 text-primary',
                  pending && 'border-border/50 bg-muted/20 text-muted-foreground',
                )}
              >
                <div className="flex items-center gap-1">
                  {done ? (
                    <Check className="h-3 w-3" />
                  ) : active ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Circle className="h-3 w-3" />
                  )}
                  <span className="font-medium">{step.label}</span>
                </div>
                <div className="pl-4 text-[10px] opacity-80">{step.blurb}</div>
              </li>
            );
          })}
        </ol>
      </div>
    </Section>
  );
}

/* ────────────────────────── Warnings ────────────────────────── */

function WarningsCard({
  warnings,
}: {
  warnings: Array<{ code: string; severity?: string; message: string }>;
}): JSX.Element {
  return (
    <Section>
      <SectionHeader>
        <div>
          <SectionTitle className="flex items-center gap-2">
            <Info className="h-4 w-4 text-muted-foreground" />
            Heads-up ({warnings.length})
          </SectionTitle>
          <SectionDescription>
            Small things we ran into during this analysis. Your report is still good to use — these are just notes on what we skipped or had to estimate.
          </SectionDescription>
        </div>
      </SectionHeader>
      <ul className="space-y-1.5">
        {warnings.map((w, i) => (
          <li
            key={i}
            className="flex items-start gap-3 rounded-md border border-amber-500/20 bg-amber-500/5 p-2 text-xs"
          >
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-amber-600" />
            <div className="flex-1 text-muted-foreground">{w.message}</div>
          </li>
        ))}
      </ul>
    </Section>
  );
}

/* ────────────────────────── Outline ────────────────────────── */

interface OutlineHint {
  h2: string;
  why: string;
  coverage_status: CompetitorClusterClassification;
  supporting_urls?: string[];
}

function OutlineCard({ outlines }: { outlines: OutlineHint[] }): JSX.Element {
  return (
    <Section>
      <SectionHeader>
        <div>
          <SectionTitle className="flex items-center gap-2">
            <ListTree className="h-4 w-4 text-primary" />
            Your article outline
          </SectionTitle>
          <SectionDescription>
            The sections to cover when writing this article — copy it to your writer or paste it into your AI tool.
          </SectionDescription>
        </div>
      </SectionHeader>
      <ol className="space-y-2.5">
        {outlines.map((h, i) => (
          <li
            key={i}
            className={cn(
              'rounded-md border border-border/70 bg-card p-3',
              h.coverage_status === 'differentiator' && 'border-l-4 border-l-emerald-500',
              h.coverage_status === 'must_have' && 'border-l-4 border-l-primary',
              h.coverage_status === 'gap' && 'border-l-4 border-l-amber-500',
              h.coverage_status === 'anti_pattern' && 'border-l-4 border-l-muted-foreground',
            )}
          >
            <div className="flex items-baseline gap-2">
              <span className="tabular text-xs font-semibold text-muted-foreground">
                H2 · {i + 1}
              </span>
              <span className="flex-1 font-medium">{h.h2}</span>
              <CoveragePill status={h.coverage_status} />
            </div>
            <div className="mt-1.5 pl-1 text-sm text-muted-foreground">{h.why}</div>
            {h.supporting_urls && h.supporting_urls.length > 0 ? (
              <ul className="mt-2 space-y-0.5 pl-1 text-xs">
                {h.supporting_urls.slice(0, 3).map((u) => (
                  <li key={u} className="flex items-center gap-1 truncate">
                    <ExternalLink className="h-3 w-3 text-muted-foreground" />
                    <a
                      href={u}
                      target="_blank"
                      rel="noreferrer"
                      className="truncate text-primary hover:underline"
                    >
                      {u}
                    </a>
                  </li>
                ))}
              </ul>
            ) : null}
          </li>
        ))}
      </ol>
    </Section>
  );
}

function CoveragePill({
  status,
}: {
  status: CompetitorClusterClassification;
}): JSX.Element {
  const meta: Record<
    CompetitorClusterClassification,
    { label: string; className: string; title: string }
  > = {
    must_have: {
      label: 'Must have',
      className: 'border-primary/30 bg-primary/10 text-primary',
      title: 'Most top-ranking pages cover this — you need it too, just to keep up.',
    },
    gap: {
      label: 'Opportunity',
      className: 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400',
      title: 'Only some top pages cover this — cover it well and you can stand out.',
    },
    differentiator: {
      label: 'Your edge',
      className: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
      title: "Rarely covered elsewhere but present in the very best results — this is where you win.",
    },
    anti_pattern: {
      label: 'Skip this',
      className: 'border-muted-foreground/30 bg-muted text-muted-foreground',
      title: 'Only weaker pages include this — leave it out.',
    },
  };
  const m = meta[status];
  return (
    <span
      title={m.title}
      className={cn(
        'rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide',
        m.className,
      )}
    >
      {m.label}
    </span>
  );
}

/* ────────────────────────── Clusters ────────────────────────── */

function ClustersSection({
  clusters,
  loading,
}: {
  clusters: CompetitorAnalysisCluster[];
  loading: boolean;
}): JSX.Element {
  const grouped: Record<CompetitorClusterClassification, CompetitorAnalysisCluster[]> = {
    must_have: [],
    gap: [],
    differentiator: [],
    anti_pattern: [],
  };
  for (const c of clusters) grouped[c.classification]?.push(c);

  return (
    <Section>
      <SectionHeader>
        <div>
          <SectionTitle className="flex items-center gap-2">
            <Layers className="h-4 w-4 text-muted-foreground" />
            What competitors talk about ({clusters.length})
          </SectionTitle>
          <SectionDescription>
            The main themes the top-ranking pages cover, sorted by how many of them touch on it.
          </SectionDescription>
        </div>
      </SectionHeader>
      {loading ? (
        <div className="flex justify-center p-4">
          <Spinner />
        </div>
      ) : clusters.length === 0 ? (
        <EmptyState
          icon={Layers}
          title="Grouping topics..."
          description="Themes will appear here once we're done reading through the competitor pages."
          variant="inline"
        />
      ) : (
        <div className="space-y-4">
          {(['must_have', 'gap', 'differentiator', 'anti_pattern'] as const).map((k) =>
            grouped[k].length > 0 ? (
              <div key={k}>
                <div className="mb-2 flex items-center gap-2">
                  <CoveragePill status={k} />
                  <span className="text-xs text-muted-foreground">
                    {grouped[k].length} topic{grouped[k].length === 1 ? '' : 's'}
                  </span>
                </div>
                <ul className="grid gap-2 md:grid-cols-2">
                  {grouped[k].map((c) => (
                    <ClusterCard key={c.id} cluster={c} />
                  ))}
                </ul>
              </div>
            ) : null,
          )}
        </div>
      )}
    </Section>
  );
}

function ClusterCard({
  cluster,
}: {
  cluster: CompetitorAnalysisCluster;
}): JSX.Element {
  const coveragePct = Math.round(cluster.coverage_rate * 100);
  return (
    <li className="rounded-md border border-border/70 bg-card p-3">
      <div className="flex items-baseline gap-2">
        <div className="min-w-0 flex-1 font-medium">{cluster.label}</div>
        <span className="tabular text-xs text-muted-foreground">
          {coveragePct}%
        </span>
      </div>
      <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-muted">
        <div
          className="h-full bg-primary"
          style={{ width: `${Math.min(100, coveragePct)}%` }}
        />
      </div>
      <div className="mt-1.5 text-[11px] text-muted-foreground">
        Avg. {cluster.avg_depth_words} words per section
      </div>
      {cluster.keywords.length > 0 ? (
        <div className="mt-2 flex flex-wrap gap-1">
          {cluster.keywords.slice(0, 6).map((k) => (
            <span
              key={k}
              className="rounded-full bg-muted px-1.5 py-0.5 text-[10px]"
            >
              {k}
            </span>
          ))}
        </div>
      ) : null}
      {Array.isArray(cluster.top_questions) && cluster.top_questions.length > 0 ? (
        <ul className="mt-2 space-y-0.5 pl-1 text-[11px] text-muted-foreground">
          {cluster.top_questions.slice(0, 2).map((q, i) => (
            <li key={i}>? {q}</li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}

/* ────────────────────────── Gaps ────────────────────────── */

function GapsSection({
  gaps,
  loading,
  showAll,
  total,
  hasMore,
  onToggleShowAll,
}: {
  gaps: CompetitorAnalysisGap[];
  loading: boolean;
  showAll: boolean;
  total: number;
  hasMore: boolean;
  onToggleShowAll: () => void;
}): JSX.Element {
  const sorted = [...gaps].sort(
    (a, b) => (b.impact_score ?? 0) - (a.impact_score ?? 0),
  );
  const canToggle = hasMore || showAll || total > 5;
  return (
    <Section>
      <SectionHeader>
        <div className="flex flex-1 flex-wrap items-start justify-between gap-3">
          <div>
            <SectionTitle className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-muted-foreground" />
              Top opportunities
              {!loading && gaps.length > 0 ? (
                <span className="font-normal text-muted-foreground">
                  ({showAll ? `all ${total}` : `top ${Math.min(5, gaps.length)}`}
                  {!showAll && total > gaps.length ? ` of ${total}` : ''})
                </span>
              ) : null}
            </SectionTitle>
            <SectionDescription>
              Calibrated content gaps ranked by impact — cover these first to compete on this SERP.
            </SectionDescription>
          </div>
          {canToggle && !loading && total > 0 ? (
            <button
              type="button"
              onClick={onToggleShowAll}
              className="rounded-md border border-border/60 px-2.5 py-1 text-xs text-muted-foreground hover:border-primary/60 hover:text-primary"
            >
              {showAll ? 'Show top 5' : 'Show all gaps'}
            </button>
          ) : null}
        </div>
      </SectionHeader>
      {loading ? (
        <div className="flex justify-center p-4">
          <Spinner />
        </div>
      ) : gaps.length === 0 ? (
        <EmptyState
          icon={AlertTriangle}
          title="No obvious gaps"
          description="The top pages are pretty thorough, or we're still working through the analysis."
          variant="inline"
        />
      ) : (
        <ul className="space-y-2">
          {sorted.map((g, idx) => (
            <li
              key={g.id}
              className="rounded-md border border-border/70 bg-card p-3"
            >
              <div className="flex items-baseline gap-2">
                {!showAll ? (
                  <span className="w-5 shrink-0 text-xs tabular text-muted-foreground">
                    {idx + 1}.
                  </span>
                ) : null}
                <span className="rounded-full border border-border/70 px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                  {g.gap_type.replace('_', ' ')}
                </span>
                <div className="flex-1 font-medium">{g.title}</div>
                {g.impact_score !== null ? (
                  <ImpactChip score={g.impact_score} />
                ) : null}
              </div>
              {g.detail ? (
                <div className="mt-1.5 text-sm text-muted-foreground">
                  {g.detail}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function ImpactChip({ score }: { score: number }): JSX.Element {
  const tone =
    score >= 70
      ? 'border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-400'
      : score >= 40
        ? 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400'
        : 'border-border/70 bg-muted text-muted-foreground';
  return (
    <span
      className={cn(
        'rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide tabular',
        tone,
      )}
    >
      impact {score}
    </span>
  );
}

/* ────────────────────────── Pages ────────────────────────── */

function PagesSection({
  pages,
  loading,
}: {
  pages: CompetitorAnalysisPage[];
  loading: boolean;
}): JSX.Element {
  const sorted = [...pages].sort((a, b) => a.rank - b.rank);
  return (
    <Section>
      <SectionHeader>
        <div>
          <SectionTitle className="flex items-center gap-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            Pages ranking today ({pages.length})
          </SectionTitle>
          <SectionDescription>
            How each of the top-ranking pages scored across the things Google looks for.
          </SectionDescription>
        </div>
      </SectionHeader>
      {loading ? (
        <div className="flex justify-center p-4">
          <Spinner />
        </div>
      ) : pages.length === 0 ? (
        <EmptyState
          icon={Search}
          title="Fetching search results..."
          description="The pages that rank on page 1 will show up as soon as we find them."
          variant="inline"
        />
      ) : (
        <ul className="space-y-2">
          {sorted.map((p) => (
            <PageCard key={p.id} page={p} />
          ))}
        </ul>
      )}
    </Section>
  );
}

function PageCard({ page }: { page: CompetitorAnalysisPage }): JSX.Element {
  const url = page.final_url ?? page.url;
  const excluded = !page.included_in_analysis;
  const eeat = page.eeat as { score_0_100?: number } | null;
  return (
    <li
      className={cn(
        'rounded-md border border-border/70 bg-card p-3',
        excluded && 'opacity-60',
      )}
    >
      <div className="flex items-baseline gap-2">
        <span className="tabular text-xs font-semibold text-muted-foreground">
          #{page.rank}
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium">
            {page.title ?? url}
          </div>
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1 truncate text-xs text-primary hover:underline"
          >
            <ExternalLink className="h-3 w-3" />
            <span className="truncate">{url}</span>
          </a>
        </div>
        {page.scorecard ? (
          <div className="flex items-center gap-1.5">
            <span className="tabular rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
              {page.scorecard.overall}
            </span>
            <span className="text-[10px] uppercase text-muted-foreground">overall</span>
          </div>
        ) : excluded ? (
          <span className="rounded-full border border-border/70 bg-muted px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
            {page.exclusion_reason ?? 'excluded'}
          </span>
        ) : null}
      </div>

      {page.scorecard ? (
        <div className="mt-3 grid grid-cols-3 gap-1.5 sm:grid-cols-6">
          <MetricChip label="Matches intent" value={page.scorecard.intent_match} />
          <MetricChip label="Structure" value={page.scorecard.structure} />
          <MetricChip label="Trust" value={page.scorecard.eeat} />
          <MetricChip label="Media" value={page.scorecard.media_richness} />
          <MetricChip label="Freshness" value={page.scorecard.freshness} />
          <MetricChip label="Angle" value={page.scorecard.unique_angle} />
        </div>
      ) : null}

      {Array.isArray(page.why_it_ranks) && page.why_it_ranks.length > 0 ? (
        <ul className="mt-3 space-y-1 pl-1 text-xs">
          {page.why_it_ranks.slice(0, 4).map((w, i) => (
            <li key={i} className="flex items-start gap-1.5 text-muted-foreground">
              <span className="mt-0.5 text-primary">·</span>
              <span>{w}</span>
            </li>
          ))}
        </ul>
      ) : null}

      {/* Deterministic quick stats */}
      <div className="mt-3 flex flex-wrap gap-3 border-t border-border/50 pt-2 text-[11px] text-muted-foreground">
        {page.word_count != null ? (
          <span>
            <span className="tabular font-medium text-foreground">
              {page.word_count.toLocaleString()}
            </span>{' '}
            words
          </span>
        ) : null}
        {page.page_type ? <span>type · {page.page_type}</span> : null}
        {page.tables > 0 ? <span>{page.tables} tables</span> : null}
        {page.faqs > 0 ? <span>{page.faqs} FAQs</span> : null}
        {page.videos > 0 ? <span>{page.videos} videos</span> : null}
        {eeat?.score_0_100 != null ? (
          <span>
            Trust{' '}
            <span className="tabular font-medium text-foreground">
              {eeat.score_0_100}
            </span>
          </span>
        ) : null}
        {page.modified_at ? (
          <span>updated {relTime(page.modified_at)}</span>
        ) : null}
      </div>
    </li>
  );
}

function MetricChip({ label, value }: { label: string; value: number }): JSX.Element {
  const tone =
    value >= 70
      ? 'text-emerald-700 dark:text-emerald-400'
      : value >= 40
        ? 'text-amber-700 dark:text-amber-400'
        : 'text-muted-foreground';
  return (
    <div className="flex flex-col rounded-md border border-border/70 bg-muted/20 p-1.5">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className={cn('tabular text-sm font-semibold', tone)}>{value}</span>
    </div>
  );
}

/* ────────────────────────── Patterns ────────────────────────── */

function PatternsCard({
  patterns,
}: {
  patterns: Record<string, unknown>;
}): JSX.Element {
  const mwc = readNumber(patterns, 'median_word_count');
  const mh2 = readNumber(patterns, 'median_h2_count');
  const mi = readNumber(patterns, 'median_images');
  const table = readNumber(patterns, 'table_presence_rate');
  const video = readNumber(patterns, 'video_presence_rate');
  const faq = readNumber(patterns, 'faq_presence_rate');
  const daysMod = readNumber(patterns, 'median_days_since_modified');
  const shared = Array.isArray(patterns['shared_entities'])
    ? (patterns['shared_entities'] as string[])
    : [];

  return (
    <Section>
      <SectionHeader>
        <div>
          <SectionTitle>Targets to hit</SectionTitle>
          <SectionDescription>
            The typical numbers across the top-ranking pages — match or beat these to compete.
          </SectionDescription>
        </div>
      </SectionHeader>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {mwc != null ? <PatternCell label="Typical length" value={`${mwc.toLocaleString()} words`} /> : null}
        {mh2 != null ? <PatternCell label="Typical sections" value={mh2} /> : null}
        {mi != null ? <PatternCell label="Typical images" value={mi} /> : null}
        {daysMod != null ? (
          <PatternCell label="Typical age" value={`${daysMod} days`} />
        ) : null}
        {table != null ? (
          <PatternCell label="Pages with tables" value={`${Math.round(table * 100)}%`} />
        ) : null}
        {video != null ? (
          <PatternCell label="Pages with videos" value={`${Math.round(video * 100)}%`} />
        ) : null}
        {faq != null ? (
          <PatternCell label="Pages with FAQs" value={`${Math.round(faq * 100)}%`} />
        ) : null}
      </div>
      {shared.length > 0 ? (
        <div className="mt-3">
          <div className="mb-2 text-xs font-medium text-muted-foreground">
            Names, brands, and terms competitors mention most
          </div>
          <div className="flex flex-wrap gap-1">
            {shared.slice(0, 15).map((e) => (
              <span
                key={e}
                className="rounded-full bg-muted px-2 py-0.5 text-[11px]"
              >
                {e}
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </Section>
  );
}

function PatternCell({
  label,
  value,
}: {
  label: string;
  value: string | number;
}): JSX.Element {
  return (
    <div className="rounded-md border border-border/70 bg-card p-3">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 text-lg font-semibold tabular">{value}</div>
    </div>
  );
}

/* ────────────────────────── helpers ────────────────────────── */

function readNumber(o: Record<string, unknown>, key: string): number | null {
  const v = o[key];
  return typeof v === 'number' ? v : null;
}
